import { ScreenHeader } from '@/components/layout/screen-header';
import { toast } from '@/components/ui/toast';
import { useAppTheme } from '@/providers/theme-provider';
import { MenuItem } from '@/types/menu-item';
import { useRouter } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { ActivityIndicator, FlatList, Pressable, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AdminPendingItemRow } from '../components/admin-pending-item-row';
import {
    useAdminApproveMenuItem,
    useAdminDeleteMenuItem,
    useAdminPendingMenuItems,
} from '../hooks/use-admin-menu-items';

const PER_PAGE = 20;

export function AdminPendingItemsScreen() {
  const theme = useAppTheme();
  const router = useRouter();
  const [page, setPage] = useState(1);

  const { data, isLoading, isFetching } = useAdminPendingMenuItems({
    page,
    per_page: PER_PAGE,
  });

  const approveMutation = useAdminApproveMenuItem();
  const deleteMutation = useAdminDeleteMenuItem();

  const items = data?.items ?? [];
  const totalPages = data?.total_pages ?? 0;

  const handleApprove = useCallback(
    (id: string) => {
      approveMutation.mutate(id);
    },
    [approveMutation]
  );

  const handleEdit = useCallback(
    (id: string) => {
      router.push(`/(root)/admin/edit-menu-item/${id}`);
    },
    [router]
  );

  const handleDelete = useCallback(
    (id: string) => {
      deleteMutation.mutate(id, {
        onError: (error: Error) => {
          toast.error(error.message || 'Failed to delete menu item');
        },
      });
    },
    [deleteMutation]
  );

  const renderItem = useCallback(
    ({ item }: { item: MenuItem }) => (
      <AdminPendingItemRow
        item={item}
        onApprove={handleApprove}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
    ),
    [handleApprove, handleEdit, handleDelete]
  );

  return (
    <SafeAreaView className="flex-1 bg-background">
      <ScreenHeader title="Pending Menu Items" showBackButton />

      <FlatList
        data={items}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 24, paddingTop: 4 }}
        ListEmptyComponent={
          isLoading ? (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <ActivityIndicator color={theme.colors.primary} />
            </View>
          ) : (
            <View style={{ paddingVertical: 40, alignItems: 'center' }}>
              <Text className="text-muted-foreground" style={{ fontSize: 15 }}>
                No pending items
              </Text>
            </View>
          )
        }
      />

      {totalPages > 1 && (
        <View
          className="border-t border-border"
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            paddingVertical: 12,
            gap: 16,
          }}
        >
          <Pressable
            disabled={page <= 1}
            onPress={() => setPage((p) => Math.max(1, p - 1))}
            accessibilityRole="button"
            accessibilityLabel="Previous page"
            style={{ opacity: page <= 1 ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Previous
            </Text>
          </Pressable>
          <Text className="text-muted-foreground" style={{ fontSize: 14 }}>
            {page} / {totalPages}
          </Text>
          <Pressable
            disabled={page >= totalPages}
            onPress={() => setPage((p) => Math.min(totalPages, p + 1))}
            accessibilityRole="button"
            accessibilityLabel="Next page"
            style={{ opacity: page >= totalPages ? 0.4 : 1 }}
          >
            <Text className="text-primary" style={{ fontSize: 15, fontWeight: '600' }}>
              Next
            </Text>
          </Pressable>
          {isFetching && <ActivityIndicator size="small" color={theme.colors.primary} />}
        </View>
      )}
    </SafeAreaView>
  );
}
